// src/auth/UserProfile.tsx

import React, { useEffect, useCallback } from 'react';
import { LogOut } from 'lucide-react';
import { useAuth, useLogout } from '../services/authService';
import { TokenManager } from '../utils/tokenManager';

// ── Proactive refresh strategy ───────────────────────────────────────────────
//
// Goal: the user is NEVER automatically logged out — only an explicit
//       "Logout" button click ends their session.
//
// How:  Every 5 minutes we check how much time the access token has left.
//       If it has ≤ 1 hour remaining, we silently call /v2/users/refresh
//       with both the access token and refresh token.  The server returns
//       a fresh access token (and sometimes a rotated refresh token) which
//       we store back into localStorage, effectively extending the session
//       indefinitely.
//
// Why 1 hour threshold?  The login API issues 365-day tokens, so refreshing
// 1 hour before expiry gives a comfortable safety window and means this
// function will only actually fire the API call roughly once per year —
// unless the server is configured with shorter expiry in other environments.
//
const REFRESH_THRESHOLD_SECONDS = 60 * 60;   // 1 hour before expiry
const CHECK_INTERVAL_MS         = 5 * 60 * 1000; // check every 5 minutes

export const UserProfile: React.FC = () => {
  const { data: authState } = useAuth();
  const logoutMutation = useLogout();

  // ── Silent proactive token refresh ─────────────────────────────────────────
  const proactiveRefresh = useCallback(async () => {
    const remaining = TokenManager.getRemainingTime();

    // Nothing to do if the token is not near expiry
    if (remaining <= 0 || remaining > REFRESH_THRESHOLD_SECONDS) return;

    const refreshToken = TokenManager.getRefreshToken();
    const accessToken  = TokenManager.getToken();

    if (!refreshToken) return;

    console.log(`🔑 Token expires in ${Math.round(remaining / 60)}m — refreshing silently…`);

    try {
      const baseUrl = import.meta.env.VITE_ENVIRONMENT === 'staging'
        ? 'https://api.staging.myautochek.com'
        : 'https://api-dev.autochek.africa';

      const res = await fetch(`${baseUrl}/v2/users/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-autochek-app': 'zoho',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
          access_token:  accessToken ?? '',
          all: true,
        }),
      });

      if (res.ok) {
        const result = await res.json();
        const { accessToken: newAccess, refreshToken: newRefresh } = result.data;

        // Persist fresh tokens — both stored in localStorage
        TokenManager.setAccessToken(newAccess);
        if (newRefresh) {
          // Server may rotate the refresh token — always store the latest one
          TokenManager.setRefreshToken(newRefresh);
        }

        console.log('✅ Session extended successfully');
      } else {
        const errData = await res.json().catch(() => ({}));
        console.warn('⚠️ Token refresh failed:', errData.message || res.statusText);

        // Only force logout if the server explicitly rejects the refresh token
        // (401 = token revoked/invalid, 403 = account suspended)
        if (res.status === 401 || res.status === 403) {
          logoutMutation.mutate();
        }
        // Any other error (network blip, 5xx) — do NOT logout, just try again
        // on the next check interval
      }
    } catch (err) {
      // Network error — do NOT logout, the user may just be temporarily offline.
      // We will try again on the next 5-minute tick.
      console.warn('⚠️ Network error during token refresh — will retry:', err);
    }
  }, [logoutMutation]);

  useEffect(() => {
    // Check immediately on mount in case the user returns to the app
    // after a long absence and the token is already near expiry
    proactiveRefresh();

    // Then check every 5 minutes in the background
    const interval = setInterval(proactiveRefresh, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [proactiveRefresh]);

  const handleLogout = () => logoutMutation.mutate();

  if (!authState?.user) return null;

  return (
    <div className="flex items-center gap-2">
      {/* Logout Button — the ONLY way the user gets logged out */}
      <button
        onClick={handleLogout}
        disabled={logoutMutation.isPending}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200 disabled:opacity-50"
        title="Logout"
      >
        <LogOut size={16} />
        <span className="hidden sm:inline">
          {logoutMutation.isPending ? 'Logging out...' : 'Logout'}
        </span>
      </button>
    </div>
  );
};